/**
 * The owner's chat history (Part 1, decision 6): Denker's history in Home
 * plus the picked chat's, hydrated once per Home and kept on a switch of
 * view. Called by `useOwnerConnection`.
 */
import { useEffect } from "react";
import { listProjectMessages } from "@/api/spaces";
import { hydrateConversationMessages } from "@/desktop/conversation-hydration";
import { hydrateConversationFrameHistory } from "@/lib/conversation-frame-history-cache";
import { isTauri } from "@/lib/is-tauri";
import { useCompanionStore } from "@/stores/companion-store";
import type { ConversationMessage } from "@/stores/conversation-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useProjectStore } from "@/stores/project-store";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { homeProjectIdOf } from "./owner-extra-projects";

/** Merge the history in; its latest run becomes the current execution. */
function applyChatHistory(messages: ConversationMessage[]): void {
  useConversationStore.getState().hydrateMessages(messages);
  const lastExecutionId = [...messages]
    .reverse()
    .find((message) => message.executionId)?.executionId;
  if (lastExecutionId)
    useCompanionStore.getState().setCurrentExecution(lastExecutionId);
}

export function useOwnerChatHistory(owner: boolean): void {
  const homeProjectId = useProjectStore(homeProjectIdOf);
  useEffect(() => {
    if (!owner || !homeProjectId) return;
    const sourceAuthScope = getDesktopChatAuthScope();
    let current = true;
    void listProjectMessages(homeProjectId)
      .then(hydrateConversationMessages)
      .then((messages) => {
        if (
          current &&
          (!isTauri() || isCurrentDesktopChatAuthScope(sourceAuthScope))
        )
          applyChatHistory(messages);
      })
      .catch(() => {});
    const conversationId = useInputBarTarget.getState().target?.conversationId;
    if (conversationId)
      void hydrateConversationFrameHistory({ conversationId });
    return () => {
      current = false;
    };
  }, [owner, homeProjectId]);
}
